<template>
    <div class="group route-date">
        <div class="row routes">
            <label>ROUTE #{{ index + 1 }}</label>
            <VSelect
                v-model="inputValue.route"
                ref="routeRef"
                :hasError="v$.route.$error"
                placeholder="Select route"
                :options="availableRoutes"
                @select="onRouteSelect"
                :clearable="false"
                class="route-select"
            />
        </div>
        <div class="row dates">
            <label>Dates</label>
            <VBtn @click="showDates = !showDates" class="dropdown-btn" color="ghost" :class="{ hasError: v$.date.$error }" type="button">
                <span>
                    {{ datesPlaceholder }}
                </span>
                <SvgIcon class="icon" icon="carbon/chevron-down" :class="{ open: showDates }" />
            </VBtn>
            <div v-if="showDates" ref="dropdownDate" class="date-pickers dates-dropdown">
                <div class="date-pickers-container">
                    <VDatePicker
                        isInline
                        :hasError="v$.date.$error"
                        ref="startDateRef"
                        class="date-picker"
                        v-model="inputValue.date"
                        :min-date="new Date()"
                        :max-date="availableDates?.endDate"
                        :disabledDates="availableDates?.disabledDates"
                        :disable="!inputValue.route.length"
                        weekdaysFormat="W"
                        :initial-page="initialPage"
                    />
                </div>
            </div>
        </div>
        <slot></slot>
    </div>
</template>

<script setup lang="ts">
import { getDatesPlaceholder, getNextValidDate, getReturnRoutes } from "@/helpers/quickformHelpers";
import useVuelidate from "@vuelidate/core";
import { computed, reactive, ref, watch } from "vue";
import useAvailableDates from "../composables/useAvailableDates";
import { routes } from "../config/routes";
import { AvailableDates } from "../models/AvailableDates";
import { MultiLegDepartureDetails } from "../models/MultiLegForm";
import { VBtn, VDatePicker, VSelect } from "v-ferry-components";
import SvgIcon from "@/components/base/SvgIcon.vue";
import { onClickOutside } from "@vueuse/core";
import { da } from "date-fns/locale";
import { getWeek } from "date-fns";
import { dateObject } from "@/helpers/dateHelpers";

interface Props {
    index: number;
    modelValue: MultiLegDepartureDetails;
    allDepartures: MultiLegDepartureDetails[];
}

const { getDates } = useAvailableDates();

const props = defineProps<Props>();

const emit = defineEmits(["update:modelValue"]);

const showDates = ref(false);

const initialPage = computed(() => {
    const date = inputValue.value.date ? new Date(inputValue.value.date) : new Date();
    return dateObject(date);
});

const inputValue = computed({
    get(): MultiLegDepartureDetails {
        return props.modelValue;
    },
    set(value: MultiLegDepartureDetails) {
        emit("update:modelValue", value);
    },
});

const state = reactive({
    route: inputValue.value.route,
    date: inputValue.value.date,
});

const rules = {
    route: {
        required: () => {
            return !!inputValue.value.route;
        },
    },
    date: {
        required: () => {
            return !!inputValue.value.date;
        },
    },
};

const v$ = useVuelidate(rules, state);

const availableDates = ref<AvailableDates>();

const routeRef = ref();

const availableRoutes = computed(() => {
    if (props.index === 0) return routes;
    return getReturnRoutes(props.allDepartures[props.index - 1].route);
});

const datesPlaceholder = computed(() => getDatesPlaceholder(inputValue.value.date));

const previousRoute = computed(() => (props.index != 0 ? props.allDepartures[props.index - 1].route : ""));

watch(previousRoute, (current) => {
    if (current) {
        inputValue.value.route = "";
    }
});

const dropdownDate = ref();

onClickOutside(dropdownDate, () => (showDates.value = false));

const onRouteSelect = (route: string) => {
    getDates(route).then((dates) => {
        availableDates.value = dates;

        if (inputValue.value.date && inputValue.value.date !== getNextValidDate(inputValue.value.date, availableDates.value.disabledDates)) {
            inputValue.value.date = undefined;
        }
    });
};
</script>

<style lang="scss" scoped>
.v-date-picker {
    padding: 15px;
}
.group {
    display: flex;
    gap: 10px;
    align-items: center;

    @include tablet {
        flex-direction: column;
        max-width: none;
        padding-top: 25px;
        border-top: 1px solid #ababab;
        transition: none;

        &:first-child:not(:has(+ .group)) {
            padding-bottom: 50px;
        }

        &:last-child {
            border-bottom: 1px solid #ababab;
        }
    }

    .row {
        width: 100%;
        max-width: 450px;

        @include desktop {
            max-width: 350px;
        }

        @include tablet {
            max-width: none;
        }

        label {
            font-size: 0.75rem;
            font-weight: bold;
            text-transform: uppercase;
        }
    }

    .routes {
        :deep(.vs__dropdown-toggle) {
            padding-top: 14px;
            padding-bottom: 12px;
        }
    }

    .dates {
        max-width: 300px;
        position: relative;
        margin-left: 30px;
        @include desktop {
            max-width: 260px;
        }

        @include tablet {
            margin-left: 0px;
            max-width: none;
        }

        :deep(.vc-container) {
            width: 100%;
            border: none;
        }

        .date-pickers {
            width: 100%;
            &-container {
                width: 100%;
            }
        }
    }

    .hasValue {
        :deep(.multiselect-placeholder) {
            color: var(--c-black);
        }
    }
}
</style>
